import { ofType } from 'redux-observable';
import {
   ignoreElements,
   filter,
   switchMap,
   takeUntil,
   distinctUntilChanged,
   take,
   map,
   distinctUntilKeyChanged,
   throttleTime,
   tap,
   mergeMap,
   mapTo,
} from 'rxjs/operators';
import { merge, of, animationFrameScheduler, from, forkJoin } from 'rxjs';
import { ajax } from 'rxjs/ajax';

import { mixer, setCrossfadeOutput } from './mixer';
import {
   SET_INSTRUMENTS,
   PIANO_ROLL_NOTE_MOUSEDOWN,
   PIANO_ROLL_MOUSEMOVE,
   popNoteIntoPending,
   placePendingNote,
   patchPendingNote,
   PIANO_ROLL_MOUSEDOWN,
   setPendingNote,
   PIANO_ROLL_NOTE_MOUSEENTER,
   removeNoteFromPart,
   PIANO_ROLL_NOTE_RESIZE_HANDLE_MOUSEDOWN,
   SET_MIXER_TRACK_PAN,
   SET_MIXER_TRACK_GAIN,
   ADD_MIXER_TRACK_EFFECT,
   UPDATE_MIXER_TRACK_EFFECT,
   TOGGLE_USER_PLAYING,
   TOGGLE_REFERENCE_PLAYING,
   LOAD_LOOP_CATEGORY_COUNT,
   addLoopCategoryCount,
   SET_CURRENT_LOOP_FROM_QUEUE,
   INSERT_INTO_LOOP_QUEUE,
   loadLoop,
   LOAD_LOOP,
   addLoop,
   TOGGLE_LOOP_MODE_PLAYING,
   TOGGLE_LOOP_MODE_REFERENCE,
   stopPlaying,
   STOP_PLAYING,
   SET_LOOP_MODE_REFERENCE,
   SET_LOOP_MODE_PLAYING,
   CLICK_NOTE_DELETE_ICON,
   PIANO_ROLL_MOUSEUP,
   SET_USER_PLAYING,
   SET_REFERENCE_PLAYING,
   ADD_INSTRUMENTS,
   SET_SEND_GAIN,
   SET_TRACK_OUT_ROUTING,
   SET_TRACK_ROUTING_ITEM,
   MOVE_MIXER_TRACK_EFFECT,
   TOGGLE_MIXER_TRACK_MUTE,
   TOGGLE_MIXER_TRACK_SOLO,
   SET_MIXER_TRACK_MUTE,
   SET_MIXER_TRACK_SOLO,
} from './actions';
import { playSequence } from './playSequence';
import { loadSoundKitEpic } from '../../shared/epics';
import { leftClickActionFilter } from '../../util/leftClickActionFilter';
import { rightClickActionFilter } from '../../util/rightClickActionFilter';
import { DOCUMENT_MOUSEUP } from '../../shared/actions';
import { combineInterfaceEpics } from '../combineInterfaceEpics';
import { audioContext } from '../../webAudio/audioContext';
import {
   crossFadeOutputTypes,
   filterParameters,
   mixerEffectTypes,
} from './constants';
import {
   calculateFilterDetune,
   calculateFilterGain,
   calculateFilterQ,
} from './util';
import { assetsUrl } from '../../util/constants';
import { getSubscriptionsFromState } from '../../util/getSubscriptionsFromState';
import { getLoadErrorFromChildren } from '../../util/getLoadErrorFromChildren';
import { loadAudioBuffer } from '../../util/loadAudioBuffer';
import { objectMap } from '../../util/objectMap';
import { playLoop } from './playLoop';
import { SET_STAGE } from '../../exercises/actions';
import { exerciseStages } from '../../shared/constants';
import { ReverbNode } from '../../webAudio/nodes/ReverbNode';

const gainControllerValueToParameteValue = (gain) => (10 ** gain - 1) / 9;

const moveNoteObservable = (action$, state$, mousedownAction) =>
   action$.pipe(
      ofType(PIANO_ROLL_MOUSEMOVE),
      throttleTime(0, animationFrameScheduler),
      distinctUntilChanged(
         (prev, curr) =>
            prev.clientX === curr.clientX && prev.clientY === curr.clientY
      ),
      map((mouseMoveAction) => ({
         x: Math.round(
            (mouseMoveAction.clientX - mousedownAction.clientX) /
               ((state$.value.activeInterface.pianoRoll.beatWidth /
                  state$.value.activeInterface.partsPerBeat) *
                  state$.value.activeInterface.pianoRoll.partSnap)
         ),
         y: Math.round(
            (mousedownAction.clientY - mouseMoveAction.clientY) /
               state$.value.activeInterface.pianoRoll.noteHeight
         ),
      })),
      distinctUntilChanged(
         (prev, curr) => prev.x === curr.x && prev.y === curr.y
      ),

      map(({ x, y }) =>
         patchPendingNote({
            startPart:
               state$.value.activeInterface.pianoRoll.forcedStartPart ??
               Math.min(
                  Math.max(
                     mousedownAction.startPart +
                        x * state$.value.activeInterface.pianoRoll.partSnap,
                     0
                  ),
                  state$.value.activeInterface.partsPerBeat *
                     state$.value.activeInterface.beatCount -
                     state$.value.activeInterface.pianoRoll.partSnap
               ),
            note:
               state$.value.activeInterface.pianoRoll.forcedNote ??
               Math.min(
                  Math.max(
                     mousedownAction.note + y,
                     state$.value.activeInterface.view.noteRange[0]
                  ),
                  state$.value.activeInterface.view.noteRange[1]
               ),
         })
      ),
      takeUntil(
         action$.pipe(
            ofType(DOCUMENT_MOUSEUP, PIANO_ROLL_MOUSEUP),
            filter(leftClickActionFilter)
         )
      )
   );

const placeNoteObservable = (action$) =>
   action$.pipe(
      ofType(DOCUMENT_MOUSEUP, PIANO_ROLL_MOUSEUP),
      filter(leftClickActionFilter),
      take(1),
      map(placePendingNote)
   );

const popNoteIntoPendingObservable = (state$, action) =>
   of(
      popNoteIntoPending(
         state$.value.activeInterface.view.instrumentIndex,
         action.startPart,
         action.note
      )
   );

const playSequenceEpic = (action$, state$) =>
   action$.pipe(
      ofType(
         TOGGLE_USER_PLAYING,
         TOGGLE_REFERENCE_PLAYING,
         SET_USER_PLAYING,
         SET_REFERENCE_PLAYING
      ),
      filter(
         () =>
            state$.value.activeInterface.userPlaying ||
            state$.value.activeInterface.referencePlaying
      ),
      mergeMap(() => playSequence(action$, state$)),
      ignoreElements()
   );

const playLoopEpic = (action$, state$) =>
   action$.pipe(
      ofType(TOGGLE_LOOP_MODE_PLAYING, SET_LOOP_MODE_PLAYING),
      filter(() => state$.value.activeInterface.loopModePlaying),
      mergeMap(() => playLoop(action$, state$)),
      ignoreElements()
   );

const stopPlayingEpic = (action$) =>
   action$.pipe(
      ofType(SET_STAGE),
      filter(({ stage }) => stage != exerciseStages.tutorial),
      mapTo(stopPlaying())
   );

const pianoRollNotePlaceEpic = (action$, state$) =>
   action$.pipe(
      ofType(PIANO_ROLL_MOUSEDOWN),
      filter(() => !state$.value.activeInterface.compareAnswer),
      filter(leftClickActionFilter),
      map((action) => ({
         ...action,
         startPart: Math.min(
            Math.max(
               Math.floor(
                  action.elementX /
                     ((state$.value.activeInterface.pianoRoll.partSnap *
                        state$.value.activeInterface.pianoRoll.beatWidth) /
                        state$.value.activeInterface.partsPerBeat)
               ) * state$.value.activeInterface.pianoRoll.partSnap,
               0
            ),
            state$.value.activeInterface.partsPerBeat *
               state$.value.activeInterface.beatCount -
               state$.value.activeInterface.pianoRoll.partSnap
         ),
         note: Math.min(
            Math.max(
               state$.value.activeInterface.view.noteRange[1] -
                  Math.floor(
                     action.elementY /
                        state$.value.activeInterface.pianoRoll.noteHeight
                  ),
               state$.value.activeInterface.view.noteRange[0]
            ),
            state$.value.activeInterface.view.noteRange[1]
         ),
      })),
      switchMap((action) =>
         merge(
            of(
               setPendingNote({
                  ...state$.value.activeInterface.pianoRoll.cachedNoteSettings,
                  startPart:
                     state$.value.activeInterface.pianoRoll.forcedStartPart ??
                     action.startPart,
                  note:
                     state$.value.activeInterface.pianoRoll.forcedNote ??
                     action.note,
                  noteLength: state$.value.activeInterface.pianoRoll.sustainAll
                     ? undefined
                     : state$.value.activeInterface.pianoRoll.cachedNoteSettings
                          .noteLength,
               })
            ),
            moveNoteObservable(action$, state$, action),
            placeNoteObservable(action$)
         )
      )
   );

const pianoRollNoteMoveEpic = (action$, state$) =>
   action$.pipe(
      ofType(PIANO_ROLL_NOTE_MOUSEDOWN),
      filter(() => !state$.value.activeInterface.compareAnswer),
      filter(leftClickActionFilter),
      switchMap((action) =>
         merge(
            popNoteIntoPendingObservable(state$, action),
            moveNoteObservable(action$, state$, action),
            placeNoteObservable(action$)
         )
      )
   );

const pianoRollNoteResizeEpic = (action$, state$) =>
   action$.pipe(
      ofType(PIANO_ROLL_NOTE_RESIZE_HANDLE_MOUSEDOWN),
      filter(() => !state$.value.activeInterface.compareAnswer),
      filter(leftClickActionFilter),
      switchMap((action) =>
         merge(
            popNoteIntoPendingObservable(state$, action),
            action$.pipe(
               ofType(PIANO_ROLL_MOUSEMOVE),
               throttleTime(0, animationFrameScheduler),
               distinctUntilKeyChanged('clientX'),
               map((mouseMoveAction) => ({
                  x: Math.round(
                     (mouseMoveAction.clientX - action.clientX) /
                        ((state$.value.activeInterface.pianoRoll.beatWidth /
                           state$.value.activeInterface.partsPerBeat) *
                           state$.value.activeInterface.pianoRoll.partSnap)
                  ),
               })),
               distinctUntilKeyChanged('x'),
               map(({ x }) =>
                  patchPendingNote({
                     noteLength: Math.max(
                        action.noteLength +
                           x * state$.value.activeInterface.pianoRoll.partSnap,
                        state$.value.activeInterface.pianoRoll.partSnap
                     ),
                  })
               ),
               takeUntil(
                  action$.pipe(
                     ofType(DOCUMENT_MOUSEUP, PIANO_ROLL_MOUSEUP),
                     filter(leftClickActionFilter)
                  )
               )
            ),
            placeNoteObservable(action$)
         )
      )
   );

const pianoRollNoteDeleteEpic = (action$, state$) =>
   merge(
      action$.pipe(
         ofType(
            PIANO_ROLL_MOUSEDOWN,
            PIANO_ROLL_NOTE_MOUSEDOWN,
            PIANO_ROLL_NOTE_RESIZE_HANDLE_MOUSEDOWN
         ),
         filter(() => !state$.value.activeInterface.compareAnswer),
         filter(rightClickActionFilter),
         switchMap((action) =>
            merge(
               of(action).pipe(
                  ofType(
                     PIANO_ROLL_NOTE_MOUSEDOWN,
                     PIANO_ROLL_NOTE_RESIZE_HANDLE_MOUSEDOWN
                  )
               ),
               action$.pipe(
                  ofType(PIANO_ROLL_NOTE_MOUSEENTER),
                  takeUntil(
                     action$.pipe(
                        ofType(DOCUMENT_MOUSEUP),
                        filter(rightClickActionFilter)
                     )
                  )
               )
            )
         ),
         map((action) =>
            removeNoteFromPart(
               state$.value.activeInterface.view.instrumentIndex,
               action.startPart,
               action.note
            )
         )
      ),
      action$.pipe(
         ofType(CLICK_NOTE_DELETE_ICON),
         map((action) =>
            removeNoteFromPart(
               state$.value.activeInterface.view.instrumentIndex,
               action.startPart,
               action.note
            )
         )
      )
   );

const crossFadeEpic = (action$, state$) =>
   action$.pipe(
      ofType(TOGGLE_LOOP_MODE_REFERENCE, SET_LOOP_MODE_REFERENCE, STOP_PLAYING),
      tap(() => {
         setCrossfadeOutput(
            state$.value.activeInterface.loopModeReference
               ? crossFadeOutputTypes.reference
               : crossFadeOutputTypes.user
         );
      }),
      ignoreElements()
   );

const createMixerTrackEffect = ({ effect: effectConfig }) => {
   const now = audioContext.currentTime;
   let effect;
   switch (effectConfig.type) {
      case mixerEffectTypes.eq: {
         effect = {
            type: effectConfig.type,
            in: audioContext.createGain(),
            out: audioContext.createGain(),
            analyzer: audioContext.createAnalyser(),
            bands: effectConfig.settings.bands.map(
               ({ type, detune, gain, Q }) => {
                  const bandFilter = audioContext.createBiquadFilter();
                  bandFilter.type = type.value;
                  bandFilter.frequency.setValueAtTime(32, now);
                  bandFilter.detune.setValueAtTime(
                     calculateFilterDetune(detune.value),
                     now
                  );
                  bandFilter.gain.setValueAtTime(
                     calculateFilterGain(gain.value),
                     now
                  );
                  bandFilter.Q.setValueAtTime(
                     calculateFilterQ(Q.value, type.value),
                     now
                  );
                  return bandFilter;
               }
            ),
         };
         [effect.in, ...effect.bands].forEach((band, i) => {
            const nextBand = effect.bands[i] || effect.analyzer;
            band.connect(nextBand);
         });
         effect.analyzer.connect(effect.out);
         effect.analyzer.smoothingTimeConstant = 0.95;
         break;
      }
      case mixerEffectTypes.balance: {
         effect = {
            type: effectConfig.type,
            in: new GainNode(audioContext),
            splitter: new ChannelSplitterNode(audioContext, {
               numberOfOutputs: 2,
            }),
            L: new GainNode(audioContext),
            R: new GainNode(audioContext),
            merger: new ChannelMergerNode(audioContext, { numberOfInputs: 2 }),
            out: new GainNode(audioContext),
         };

         effect.in.connect(effect.splitter);

         effect.splitter.connect(effect.L, 0);
         effect.splitter.connect(effect.R, 1);
         effect.L.connect(effect.merger, 0, 0);
         effect.R.connect(effect.merger, 0, 1);
         effect.merger.connect(effect.out);

         effect.L.gain.setValueAtTime(1, now);
         effect.R.gain.setValueAtTime(1, now);
         break;
      }
      case mixerEffectTypes.width: {
         effect = {
            type: effectConfig.type,
            in: new GainNode(audioContext),
            splitter: new ChannelSplitterNode(audioContext, {
               numberOfOutputs: 2,
            }),

            invertedR: new GainNode(audioContext, { gain: -1 }),
            mid: new GainNode(audioContext, {
               gain: 0.5,
            }),
            side: new GainNode(audioContext, {
               gain: 0.5,
            }),
            invertedSide: new GainNode(audioContext, { gain: -1 }),
            merger: new ChannelMergerNode(audioContext, { numberOfInputs: 2 }),
            out: new GainNode(audioContext),
         };
         effect.in.connect(effect.splitter);

         effect.splitter.connect(effect.mid, 0);
         effect.splitter.connect(effect.mid, 1);
         effect.splitter.connect(effect.invertedR, 1);

         effect.splitter.connect(effect.side, 0);
         effect.invertedR.connect(effect.side);
         effect.side.connect(effect.invertedSide);

         effect.mid.connect(effect.merger, 0, 0);
         effect.side.connect(effect.merger, 0, 0);

         effect.mid.connect(effect.merger, 0, 1);
         effect.invertedSide.connect(effect.merger, 0, 1);

         effect.merger.connect(effect.out);
         break;
      }
      case mixerEffectTypes.delay: {
         effect = {
            type: effectConfig.type,
            in: new GainNode(audioContext),

            delayGain: new GainNode(audioContext),
            delay: new DelayNode(audioContext),

            pingPongSplitter: new ChannelSplitterNode(audioContext, {
               numberOfOutputs: 2,
            }),
            pingPongMerger: new ChannelMergerNode(audioContext, {
               numberOfInputs: 2,
            }),
            standardGain: new GainNode(audioContext), // standard == not ping pong
            pingPongGain: new GainNode(audioContext),

            delay: new DelayNode(audioContext),

            feedbackIn: new GainNode(audioContext),

            out: new GainNode(audioContext),
         };

         effect.delayGain.gain.setValueAtTime(
            effectConfig.settings.delayGain.value,
            now
         );
         effect.delay.delayTime.setValueAtTime(
            effectConfig.settings.delayTime.value,
            now
         );
         effect.feedbackIn.gain.setValueAtTime(
            effectConfig.settings.feedbackGain.value,
            now
         );
         effect.standardGain.gain.setValueAtTime(
            effectConfig.settings.pingPong.value ? 0 : 1,
            now
         );
         effect.pingPongGain.gain.setValueAtTime(
            effectConfig.settings.pingPong.value ? 1 : 0,
            now
         );

         effect.in.connect(effect.out);

         effect.in.connect(effect.delayGain);
         effect.delayGain.connect(effect.standardGain);
         effect.standardGain.connect(effect.delay);

         effect.delayGain.connect(effect.pingPongSplitter);
         effect.pingPongSplitter.connect(effect.pingPongMerger, 0, 1);
         effect.pingPongSplitter.connect(effect.pingPongMerger, 1, 0);
         effect.pingPongMerger.connect(effect.pingPongGain);
         effect.pingPongGain.connect(effect.delay);

         effect.delay.connect(effect.feedbackIn);
         effect.feedbackIn.connect(effect.standardGain);
         effect.feedbackIn.connect(effect.pingPongSplitter);

         effect.delay.connect(effect.out);

         break;
      }
      case mixerEffectTypes.reverb: {
         effect = new ReverbNode({
            decay: effectConfig.settings.decay?.value,
            predelay: effectConfig.settings.predelay?.value,
            color: effectConfig.settings.color?.value,
            wet: effectConfig.settings.wet?.value,
            dry: effectConfig.settings.dry?.value,
            preWidth: effectConfig.settings.preWidth?.value,
         });

         break;
      }
      default: {
         return null;
      }
   }
   return effect;
};

const createMixerTrack = ({ mixerTrack }) => {
   if (mixerTrack == null) {
      return null;
   }

   const now = audioContext.currentTime;
   const track = {
      in: audioContext.createGain(),
      effects: {
         in: audioContext.createGain(),
         effects: [],
         out: audioContext.createGain(),
      },
      pan: audioContext.createStereoPanner(),
      gain: audioContext.createGain(),
      split: audioContext.createChannelSplitter(2),
      analyzerL: audioContext.createAnalyser(),
      analyzerR: audioContext.createAnalyser(),
      merge: audioContext.createChannelMerger(2),
      out: audioContext.createGain(),
   };
   track.in.connect(track.effects.in);
   track.effects.out.connect(track.pan);
   track.pan.connect(track.gain);
   track.gain.connect(track.split);
   track.split.connect(track.analyzerL, 0);
   track.split.connect(track.analyzerR, 1);
   track.analyzerL.connect(track.merge, 0, 0);
   track.analyzerR.connect(track.merge, 0, 1);
   track.analyzerL.fftSize = 2048;
   track.analyzerR.fftSize = 2048;
   track.merge.connect(track.out);

   track.gain.gain.setValueAtTime(
      gainControllerValueToParameteValue(mixerTrack.gain.value),
      now
   );

   track.pan.pan.setValueAtTime(mixerTrack.pan.value, now);

   mixerTrack.effects.forEach((effectConfig, i) => {
      const effect = createMixerTrackEffect({ effect: effectConfig });

      track.effects.effects.push(effect);

      // connections
      if (i === 0) {
         track.effects.in.connect(effect.in);
      } else {
         track.effects.effects[i - 1].out.connect(effect.in);
      }
   });

   if (track.effects.effects.length === 0) {
      track.effects.in.connect(track.effects.out);
   } else {
      track.effects.effects[track.effects.effects.length - 1].out.connect(
         track.effects.out
      );
   }

   return track;
};

const setInstrumentsEpic = (action$, state$) =>
   action$.pipe(
      ofType(SET_INSTRUMENTS, ADD_INSTRUMENTS),
      tap((action) => {
         const now = audioContext.currentTime;

         const kit =
            state$.value.activeInterface.soundKits[
               state$.value.activeInterface.soundKit?.id
            ];

         const tracksToProcess =
            action.type === ADD_INSTRUMENTS
               ? state$.value.activeInterface.instrumentRack.slice(
                    state$.value.activeInterface.instrumentRack.length -
                       action.instruments.length
                 )
               : state$.value.activeInterface.instrumentRack;

         const processedTracks = tracksToProcess.map(
            ({ source, pregain, mixerTrack, referenceMixerTrack }) => {
               const track = {
                  pregain: audioContext.createGain(),
                  track: createMixerTrack({ mixerTrack }),
                  referenceTrack: createMixerTrack({
                     mixerTrack: referenceMixerTrack,
                  }),
               };

               track.pregain.connect(track.track.in);

               let pregainGainValue = 0.5;

               if (kit != null && kit.sounds[source]?.pregain != null) {
                  pregainGainValue = kit.sounds[source]?.pregain;
               }

               track.pregain.gain.setValueAtTime(
                  pregain != null ? pregain : pregainGainValue,
                  now
               );

               if (mixerTrack.sendOut !== false) {
                  track.track.out.connect(mixer.crossFader.user);
               }

               if (track.referenceTrack != null) {
                  track.pregain.connect(track.referenceTrack.in);
                  track.referenceTrack.out.connect(mixer.crossFader.reference);
               }

               return track;
            }
         );

         if (action.type === ADD_INSTRUMENTS) {
            mixer.tracks.push(...processedTracks);
         } else {
            mixer.tracks = processedTracks;
         }
      }),
      ignoreElements()
   );

const setMixerTrackPanEpic = (action$) =>
   action$.pipe(
      ofType(SET_MIXER_TRACK_PAN),
      tap((action) => {
         const now = audioContext.currentTime;
         const panParam = mixer.tracks[action.instrumentIndex].track.pan.pan;
         panParam.setValueAtTime(panParam.value, now);
         panParam.linearRampToValueAtTime(action.pan, now + 0.01);
      }),
      ignoreElements()
   );

const setMixerTrackGainEpic = (action$) =>
   action$.pipe(
      ofType(SET_MIXER_TRACK_GAIN),
      tap((action) => {
         const now = audioContext.currentTime;
         const gainParam = mixer.tracks[action.instrumentIndex].track.gain.gain;
         gainParam.setValueAtTime(gainParam.value, now);
         gainParam.linearRampToValueAtTime(
            // action.gain ** 2,
            gainControllerValueToParameteValue(action.gain),
            audioContext.currentTime + 0.01
         );
      }),
      ignoreElements()
   );

const addMixerTrackEffectEpic = (action$) =>
   action$.pipe(
      ofType(ADD_MIXER_TRACK_EFFECT),
      tap((action) => {
         const effect = createMixerTrackEffect(action);

         const currentTrack = mixer.tracks[action.instrumentIndex].track;
         currentTrack.effects.effects.push(effect);
         currentTrack.effects.in.disconnect();
         currentTrack.effects.in.connect(
            currentTrack.effects.effects[0]?.in || currentTrack.effects.out
         );
         currentTrack.effects.effects.forEach((currentEffect, i) => {
            const nextEffect =
               currentTrack.effects.effects[i + 1]?.in ||
               currentTrack.effects.out;
            currentEffect.out.disconnect();
            currentEffect.out.connect(nextEffect);
         });
      }),
      ignoreElements()
   );

const updateMixerTrackEffectEpic = (action$, state$) =>
   action$.pipe(
      ofType(UPDATE_MIXER_TRACK_EFFECT),
      tap((action) => {
         const now = audioContext.currentTime;

         const effect =
            state$.value.activeInterface.instrumentRack[action.instrumentIndex]
               .mixerTrack.effects[action.effectIndex];

         switch (effect.type) {
            case mixerEffectTypes.eq: {
               Object.entries(
                  action.update.bands?.$set ?? action.update.bands
               ).forEach(([bandIndex, bandSettings]) => {
                  Object.entries(bandSettings).forEach(
                     ([parameter, parameterObject]) => {
                        const value =
                           parameterObject.value?.$set ?? parameterObject.value;
                        const filterNode =
                           mixer.tracks[action.instrumentIndex][
                              action.reference ? 'referenceTrack' : 'track'
                           ].effects.effects[action.effectIndex].bands[
                              bandIndex
                           ];

                        if (parameter === 'type') {
                           filterNode[parameter] = value;
                           if (bandSettings.Q?.value?.$set == null) {
                              filterNode.Q.linearRampToValueAtTime(
                                 calculateFilterQ(
                                    effect.settings.bands[bandIndex].Q.value,
                                    value
                                 ),
                                 now + 0.01
                              );
                           }
                        } else {
                           let nextValue = value;
                           if (parameter === filterParameters.detune) {
                              nextValue = calculateFilterDetune(value);
                           } else if (parameter === filterParameters.gain) {
                              nextValue = calculateFilterGain(value);
                           } else if (parameter === filterParameters.Q) {
                              const filterType =
                                 bandSettings.type?.value?.$set ||
                                 effect.settings.bands[bandIndex].type.value;
                              nextValue = calculateFilterQ(value, filterType);
                           }
                           filterNode[parameter].setValueAtTime(
                              filterNode[parameter].value,
                              now
                           );
                           filterNode[parameter].linearRampToValueAtTime(
                              nextValue,
                              now + 0.01
                           );
                        }
                     }
                  );
               });

               break;
            }
            case mixerEffectTypes.balance: {
               const balance = action.update.balance.value.$set;

               const effect =
                  mixer.tracks[action.instrumentIndex].track.effects.effects[
                     action.effectIndex
                  ];
               const leftNode = effect.L;
               leftNode.gain.setValueAtTime(leftNode.gain.value, now);

               const rightNode = effect.R;
               rightNode.gain.setValueAtTime(rightNode.gain.value, now);

               if (balance <= 0) {
                  leftNode.gain.linearRampToValueAtTime(1, now + 0.01);
                  rightNode.gain.linearRampToValueAtTime(
                     1 + balance,
                     now + 0.01
                  );
               } else {
                  rightNode.gain.linearRampToValueAtTime(1, now + 0.01);
                  leftNode.gain.linearRampToValueAtTime(
                     1 - balance,
                     now + 0.01
                  );
               }

               break;
            }
            case mixerEffectTypes.width: {
               const width = action.update.width.$set;
               const sideNode =
                  mixer.tracks[action.instrumentIndex].track.effects.effects[
                     action.effectIndex
                  ].side;

               sideNode.gain.setValueAtTime(sideNode.gain.value, now);

               if (width <= 0.5) {
                  sideNode.gain.linearRampToValueAtTime(
                     Math.log10(9 * 0.24 * 2 * width + 1),
                     now + 0.01
                  );
               } else {
                  sideNode.gain.linearRampToValueAtTime(width, now + 0.01);
               }

               break;
            }
            case mixerEffectTypes.reverb: {
               const effect =
                  mixer.tracks[action.instrumentIndex].track.effects.effects[
                     action.effectIndex
                  ];
               Object.entries(action.update).forEach(
                  ([
                     parameter,
                     {
                        value: { $set: value },
                     },
                  ]) => {
                     if (
                        parameter === 'decay' ||
                        parameter === 'preWidth' ||
                        parameter === 'color'
                     ) {
                        //ToDO: Debounce
                        // todo : may need to apply curves, etc.
                        effect[parameter] = value;
                     } else if (parameter === 'predelay') {
                        // toDO, set range and curve  appropriately
                        effect.predelay.delayTime.linearRampToValueAtTime(
                           value,
                           now + 0.01
                        );
                     } else {
                        // ToDo - maybe make these Power based (squared) or something
                        // basic gain changes
                        effect[parameter].gain.linearRampToValueAtTime(
                           value,
                           now + 0.01
                        );
                     }
                  }
               );

               break;
            }
            default: {
               break;
            }
         }
      }),
      ignoreElements()
   );

const moveMixerTrackEffectEpic = (action$, state$) =>
   action$.pipe(
      ofType(MOVE_MIXER_TRACK_EFFECT),
      tap((action) => {
         const currentTrack =
            mixer.tracks[state$.value.activeInterface.view.instrumentIndex]
               .track;
         const effects = currentTrack.effects.effects;
         const [activeEffect] = effects.splice(action.activeIndex, 1);
         effects.splice(action.overIndex, 0, activeEffect);

         currentTrack.effects.in.disconnect();
         currentTrack.effects.in.connect(effects[0]?.in);

         effects.forEach((currentEffect, i) => {
            const nextEffect =
               currentTrack.effects.effects[i + 1]?.in ??
               currentTrack.effects.out;
            currentEffect.out.disconnect();
            currentEffect.out.connect(nextEffect);
         });
      }),
      ignoreElements()
   );

const loadLoopCategoryCountEpic = (action$, state$) =>
   action$.pipe(
      ofType(LOAD_LOOP_CATEGORY_COUNT),
      mergeMap((action) => {
         const activeSubscriptions = getSubscriptionsFromState(state$.value);
         return ajax({
            url: `${assetsUrl}/${
               activeSubscriptions.exercise ? 'secure' : 'public'
            }/exercise/audio/loopKit/${action.loopKitId}/category/${
               action.categoryId
            }/count.json`,
            headers: {
               Authorization: `Bearer ${state$.value.appData.permissions.token}`,
            },
            // withCredentials: true,
            // crossDomain: true,
         }).pipe(
            map(({ response }) =>
               addLoopCategoryCount({
                  loopKitId: action.loopKitId,
                  categoryId: action.categoryId,
                  count: response.count,
               })
            )
         );
      })
   );

const preloadQueuedLoopsEpic = (action$, state$) =>
   action$.pipe(
      ofType(SET_CURRENT_LOOP_FROM_QUEUE, INSERT_INTO_LOOP_QUEUE),
      mergeMap(() => {
         const currentState = state$.value;

         const firstTwoQueueItems =
            currentState.activeInterface.loops.queue.slice(0, 2);
         const firstNextLevelQueueItem =
            currentState.activeInterface.loops.nextLevelQueue.slice(0, 1);

         const toLoad = Object.values(
            firstTwoQueueItems
               .concat(firstNextLevelQueueItem)
               .reduce((itemMap, currentItem) => {
                  const key = `${currentItem.loopKitId}/${currentItem.categoryId}/${currentItem.loopId}`;

                  if (currentState.activeInterface.loops.cache[key] == null) {
                     return {
                        ...itemMap,
                        [key]: currentItem,
                     };
                  }
                  return itemMap;
               }, {})
         );

         return from(toLoad);
      }),
      map((loopData) => loadLoop(loopData))
   );

const loadLoopSample = (sound) =>
   loadAudioBuffer(sound.sourceFilePath).pipe(
      map((buffer) => ({
         ...sound,
         loadError: buffer == null,
         buffer,
      }))
   );

const loadLoopEpic = (action$, state$) =>
   action$.pipe(
      ofType(LOAD_LOOP),
      mergeMap((action) => {
         const activeSubscriptions = getSubscriptionsFromState(state$.value);
         return ajax({
            url: `${assetsUrl}/${
               activeSubscriptions.exercise ? 'secure' : 'public'
            }/exercise/audio/loopKit/${action.loopKitId}/category/${
               action.categoryId
            }/loop/${action.loopId}.json`,
            headers: {
               Authorization: `Bearer ${state$.value.appData.permissions.token}`,
            },
            // withCredentials: true,
            // crossDomain: true,
         }).pipe(
            switchMap(({ response }) =>
               of({
                  cacheId: `${action.loopKitId}/${action.categoryId}/${action.loopId}`,
                  loop: response,
               })
            )
         );
      }),
      // load audio files
      mergeMap((action) =>
         forkJoin(
            objectMap(action.loop.samples, (sample) => loadLoopSample(sample))
         ).pipe(
            map((samples) => {
               const { cacheId, loop } = action;
               return addLoop(cacheId, {
                  ...loop,
                  loadError: getLoadErrorFromChildren(samples),
                  lastAccessed: Date.now(),

                  samples,
               });
            })
         )
      )
   );

const setTrackRoutingItemEpic = (action$) =>
   action$.pipe(
      ofType(SET_TRACK_ROUTING_ITEM),
      tap((action) => {
         const fromTrack = mixer.tracks[action.fromTrackIndex].track;
         const toTrack = mixer.tracks[action.toTrackIndex].track;

         if (action.isRouted) {
            if (fromTrack.sends == null) {
               fromTrack.sends = {};
            }

            const now = audioContext.currentTime;
            const sendGain = audioContext.createGain();
            sendGain.gain.setValueAtTime(1, now);

            fromTrack.out.connect(sendGain);
            sendGain.connect(toTrack.in);

            fromTrack.sends[action.toTrackIndex] = sendGain;
         } else {
            fromTrack.out.disconnect(fromTrack.sends[action.toTrackIndex]);
            fromTrack.sends[action.toTrackIndex].disconnect();
            fromTrack.sends[action.toTrackIndex] = null;
         }
      }),
      ignoreElements()
   );

const setTrackOutRoutingEpic = (action$) =>
   action$.pipe(
      ofType(SET_TRACK_OUT_ROUTING),
      tap((action) => {
         const track = mixer.tracks[action.trackIndex].track;

         if (action.isRouted) {
            track.out.connect(mixer.crossFader.user);
         } else {
            track.out.disconnect(mixer.crossFader.user);
         }
      }),
      ignoreElements()
   );

const setSendGainEpic = (action$) =>
   action$.pipe(
      ofType(SET_SEND_GAIN),
      tap((action) => {
         const now = audioContext.currentTime;
         const gainParam =
            mixer.tracks[action.fromTrackIndex].track.sends[action.toTrackIndex]
               .gain;

         gainParam.linearRampToValueAtTime(
            gainControllerValueToParameteValue(action.gain),
            now + 0.01
         );
      }),
      ignoreElements()
   );

const getSentTrackIndexesRecursively = (
   trackIndex,
   trackList,
   indexMap = {}
) => {
   const newIndexes = [];

   const sends = trackList[trackIndex].mixerTrack.sendTo;

   if (sends == null) {
      return null;
   }

   Object.keys(sends).forEach((id) => {
      const index = trackList.findIndex((track) => track.id === id);
      indexMap[index] = true;
      newIndexes.push(index);
   });

   // if feedback loop this is infinite, but also if feedback loop ears are ded
   newIndexes.forEach((index) => {
      getSentTrackIndexesRecursively(index, trackList, indexMap);
   });

   return indexMap;
};

const muteSoloEpic = (action$, state$) =>
   action$.pipe(
      ofType(
         TOGGLE_MIXER_TRACK_MUTE,
         TOGGLE_MIXER_TRACK_SOLO,
         SET_MIXER_TRACK_MUTE,
         SET_MIXER_TRACK_SOLO
      ),
      tap(() => {
         const { soloTrack, instrumentRack } = state$.value.activeInterface;
         const now = audioContext.currentTime;

         const sentTrackIndexes = {};

         if (soloTrack != null) {
            getSentTrackIndexesRecursively(
               soloTrack,
               instrumentRack,
               sentTrackIndexes
            );
         }

         instrumentRack.forEach(({ mixerTrack: { muted } }, i) => {
            const isMuted =
               muted ||
               (soloTrack != null && soloTrack !== i && !sentTrackIndexes[i]);
            const gainParam = mixer.tracks[i].track.in.gain;

            if (isMuted && gainParam.value !== 0) {
               gainParam.setValueAtTime(1, now);
               gainParam.linearRampToValueAtTime(0, now + 0.01);
            }
            if (!isMuted && gainParam.value !== 1) {
               gainParam.setValueAtTime(0, now);
               gainParam.linearRampToValueAtTime(1, now + 0.01);
            }
         });
      }),
      ignoreElements()
   );

export const sequencerEpics = combineInterfaceEpics(
   playSequenceEpic,
   playLoopEpic,
   stopPlayingEpic,
   pianoRollNoteDeleteEpic,
   pianoRollNoteMoveEpic,
   pianoRollNoteResizeEpic,
   pianoRollNotePlaceEpic,
   loadSoundKitEpic,
   setInstrumentsEpic,
   setMixerTrackPanEpic,
   setMixerTrackGainEpic,
   addMixerTrackEffectEpic,
   updateMixerTrackEffectEpic,
   moveMixerTrackEffectEpic,
   loadLoopCategoryCountEpic,
   preloadQueuedLoopsEpic,
   loadLoopEpic,
   crossFadeEpic,
   setTrackRoutingItemEpic,
   setTrackOutRoutingEpic,
   setSendGainEpic,
   muteSoloEpic
);
